import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// All page-level dynamic imports use lazyWithRetry instead of React.lazy
// directly. After every deploy, browsers (Safari most aggressively) can
// serve a stale index.html that references chunk hashes which no longer
// exist on origin — the lazy import 404s and ErrorBoundary shows a
// "Something went wrong" page until the user manually hard-refreshes.
// lazyWithRetry catches that specific failure and does a one-shot
// window.location.reload() to fetch fresh assets, transparently
// recovering from stale-cache deploys. See the file for the full
// rationale and the retry-loop guard. (Bug investigation 2026-04-30:
// Safari users hit "Something went wrong" after every deploy.)
import lazyWithRetry from './lib/lazyWithRetry';
import ScrollToTop       from './components/ScrollToTop';
import PageTransition    from './components/PageTransition';
import StagingBanner   from './components/StagingBanner';
import AnnouncementBar from './components/AnnouncementBar';
import Navbar       from './components/Navbar';
// Footer was lazy() up to PSI run 11:33 AM 2026-04-29. Mobile CLS
// spiked to 0.165 (from 0.063) with the footer flagged as the
// dominant shift element at 0.160. Diagnosis: on slow-4G mobile
// the Footer chunk frequently resolved BEFORE the route's main
// chunk, so the footer briefly rendered immediately below the
// 60vh route Suspense fallback, then the route chunk landed,
// expanded the main column to its real ~300vh height, and pushed
// the footer down by 200vh+. Lighthouse credits that move as
// cumulative layout shift on the footer element. Desktop didn't
// see this because chunks arrive close enough together that the
// footer never paints in the wrong place.
//
// Fix: eager-import Footer so it's part of main.js (one fewer
// chunk, deterministic render order, predictable layout). Cost
// is ~5 KiB extra in main.js parse — the CLS win dominates the
// FCP/TBT cost on mobile. Keeping CookieBanner / StickyApply /
// GoogleAnalytics lazy because they're position:fixed (no layout
// shift contribution) or render null on first paint.
import Footer from './components/Footer';

// Below-fold and post-interaction shell pieces stay lazy so they
// don't inflate the initial main.js bundle. CookieBanner renders
// null on first paint, StickyApply is position:fixed, GoogleAnalytics
// is a <script> tag — none of them contribute to layout shift.
const CookieBanner     = lazyWithRetry(() => import('./components/CookieBanner'));
const StickyApply      = lazyWithRetry(() => import('./components/StickyApply'));
const GoogleAnalytics  = lazyWithRetry(() => import('./components/GoogleAnalytics'));

const Home           = lazyWithRetry(() => import('./pages/Home'));
const About          = lazyWithRetry(() => import('./pages/About'));
const FindCourse     = lazyWithRetry(() => import('./pages/FindCourse'));
const StudentGuides  = lazyWithRetry(() => import('./pages/StudentGuides'));
const Contact        = lazyWithRetry(() => import('./pages/Contact'));
const PrivacyPolicy  = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const ModernSlavery  = lazyWithRetry(() => import('./pages/ModernSlavery'));
const LegalDisclaimer = lazyWithRetry(() => import('./pages/LegalDisclaimer'));
const CookiesPolicy  = lazyWithRetry(() => import('./pages/CookiesPolicy'));
const WorkWithUs     = lazyWithRetry(() => import('./pages/WorkWithUs'));
const Services       = lazyWithRetry(() => import('./pages/Services'));
const CourseDetail   = lazyWithRetry(() => import('./pages/CourseDetail'));
const JobDetail      = lazyWithRetry(() => import('./pages/JobDetail'));
const Apply          = lazyWithRetry(() => import('./pages/Apply'));
const ComingSoon     = lazyWithRetry(() => import('./pages/ComingSoon'));
const Partners       = lazyWithRetry(() => import('./pages/Partners'));
const SuccessStories = lazyWithRetry(() => import('./pages/SuccessStories'));
const Events         = lazyWithRetry(() => import('./pages/Events'));
const CountryGuide   = lazyWithRetry(() => import('./pages/CountryGuide'));
const Blog           = lazyWithRetry(() => import('./pages/Blog'));
const BlogPost       = lazyWithRetry(() => import('./pages/BlogPost'));
const FAQ            = lazyWithRetry(() => import('./pages/FAQ'));
const Funding        = lazyWithRetry(() => import('./pages/Funding'));
const NotFound       = lazyWithRetry(() => import('./pages/NotFound'));

// /agent-portal and /student-portal routes. When REACT_APP_PORTALS_LIVE
// is "true" they redirect to the CRM Next.js auth pages; otherwise they
// render the in-website "Coming Soon" page (the default — flip the flag
// once the CRM auth flow is actually serving traffic).
const CRM_URL = process.env.REACT_APP_CRM_URL || '';
const PORTALS_LIVE = process.env.REACT_APP_PORTALS_LIVE === 'true' && !!CRM_URL;
function PortalRedirect({ to, fallback }) {
  useEffect(() => {
    if (PORTALS_LIVE) {
      window.location.replace(`${CRM_URL}${to}`);
    }
  }, [to]);
  if (PORTALS_LIVE) return null; // browser is redirecting
  return fallback;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={null}><GoogleAnalytics /></Suspense>
      <StagingBanner />
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <AnnouncementBar />
      <Navbar />

      <main id="main-content">
        <PageTransition>
        {/* Suspense fallback height tuned to be CLOSE to the actual
            route content height. The previous 60vh undershot drastically
            (real route content is ~300vh on /), so when the route chunk
            landed the document body grew by ~240vh and the footer (which
            was momentarily painted near the top of the viewport on slow
            mobile builds) snapped down — Lighthouse credited that to a
            0.16 CLS shift on <footer>. 100vh closes the gap enough that
            the footer's initial paint position approximates its final
            position to within one viewport, capping the shift well below
            the 0.1 CLS warning threshold even when the chunk-order
            ordering is unfavourable. */}
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <Routes>
            <Route path="/"                       element={<Home />} />
            <Route path="/about"                  element={<About />} />
            <Route path="/courses"                element={<Navigate to="/find-course" replace />} />
            <Route path="/find-course"            element={<FindCourse />} />
            <Route path="/student-guides"         element={<StudentGuides />} />
            <Route path="/contact"                element={<Contact />} />
            <Route path="/privacy-policy"         element={<PrivacyPolicy />} />
            <Route path="/modern-slavery"         element={<ModernSlavery />} />
            <Route path="/legal-disclaimer"       element={<LegalDisclaimer />} />
            <Route path="/cookies-policy"         element={<CookiesPolicy />} />
            <Route path="/work-with-us"           element={<WorkWithUs />} />
            <Route path="/services"               element={<Services />} />
            <Route path="/apply"                   element={<Apply />} />
            <Route path="/course/:id"             element={<CourseDetail />} />
            <Route path="/careers/:id"            element={<JobDetail />} />
            <Route path="/coming-soon"            element={<ComingSoon />} />
            {/* Portal redirects — Navbar/Footer Login buttons now point
                directly at the CRM, but these routes remain so any old
                bookmarks of the placeholder URLs still resolve to the
                right place. PortalRedirect falls back to the ComingSoon
                page when the CRM URL isn't configured (e.g. local dev). */}
            <Route path="/agent-portal"           element={<PortalRedirect to="/login"         fallback={<ComingSoon title="Agent Portal"   subtitle="We're building a dedicated space for our education agents to manage applications, track students, and access marketing resources." />} />} />
            <Route path="/student-portal"         element={<PortalRedirect to="/student-login" fallback={<ComingSoon title="Student Portal" subtitle="We're building a dedicated student portal where you'll track your application, upload documents, and chat with your admissions officer." />} />} />
            <Route path="/partners"               element={<Partners />} />
            <Route path="/success-stories"        element={<SuccessStories />} />
            <Route path="/events"                 element={<Events />} />
            <Route path="/country-guide/:country" element={<CountryGuide />} />
            <Route path="/blog"                   element={<Blog />} />
            <Route path="/blog/:id"               element={<BlogPost />} />
            <Route path="/faq"                    element={<FAQ />} />
            <Route path="/funding"                element={<Funding />} />
            <Route path="*"                       element={<NotFound />} />
          </Routes>
        </Suspense>
        </PageTransition>
      </main>

      {/* Footer is now eager-imported above (fixes mobile CLS — see
          comment near the import). StickyApply and CookieBanner stay
          lazy: StickyApply is position:fixed (zero layout impact)
          and CookieBanner renders null on first paint. */}
      <Suspense fallback={null}>
        <StickyApply />
        <CookieBanner />
      </Suspense>
      <Footer />
    </>
  );
}
